import { useState } from 'react';
import { Box, Button, Container, TextField, Typography, Paper } from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import Dashboard from './pages/Dashboard';
import Sidebar from './components/Sidebar';
import SystemSelect from './pages/SystemSelect';
import Assets from './pages/Assets';
import DeviceDetail from './pages/DeviceDetail';
import AssetGroup from './pages/AssetGroup';
import Vulnerabilities from './pages/Vulnerabilities';
import StigCompliance from './pages/StigCompliance';
import Alerts from './pages/Alerts';
import ComplyToConnect from './pages/ComplyToConnect';
import AtoRmf from './pages/AtoRmf';
import AtoExport from './pages/AtoExport';
import DmzTopology from './pages/DmzTopology';
import AiAssistant from './pages/AiAssistant';
import Architecture from './pages/Architecture';
import Layout from './components/Layout';
import logo from './assets/logo.jpeg';

function App() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loggedIn, setLoggedIn] = useState(false);
  const [selectedSystem, setSelectedSystem] = useState(null);
  const [activePage, setActivePage] = useState('dashboard');
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceContext, setDeviceContext] = useState(null);

  const navigateToDevice = (device, context = null) => {
    setSelectedDevice(device);
    setDeviceContext(context);
    setActivePage('device');
  };

  const [selectedGroup, setSelectedGroup] = useState(null);
  const navigateToGroup = (group) => {
    setSelectedGroup(group);
    setActivePage('assetGroup');
  };

  const [localOverrides, setLocalOverrides] = useState({});
  const [customRanges, setCustomRanges] = useState([]);

  const handleLogin = () => {
    if (username && password) setLoggedIn(true);
  };

  if (loggedIn && !selectedSystem) return <SystemSelect onSelect={setSelectedSystem} />;

  if (loggedIn) return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar activePage={activePage} setActivePage={setActivePage} />
      <Box sx={{ marginLeft: '64px', flex: 1 }}>
        <Layout system={selectedSystem} onSystemChange={() => setSelectedSystem(null)} activePage={activePage} onNavigateDashboard={() => setActivePage('dashboard')} onNavigate={setActivePage}>
          {activePage === 'dashboard' && <Dashboard setActivePage={setActivePage} onSelectDevice={navigateToDevice} onSelectGroup={navigateToGroup} />}
          {activePage === 'assets' && <Assets onSelectDevice={navigateToDevice} onSelectGroup={navigateToGroup} localOverrides={localOverrides} setLocalOverrides={setLocalOverrides} customRanges={customRanges} setCustomRanges={setCustomRanges} />}
          {activePage === 'device' && <DeviceDetail device={selectedDevice} context={deviceContext} onBack={() => setActivePage('assets')} onBackToGroup={() => setActivePage('assetGroup')} />}
          {activePage === 'assetGroup' && <AssetGroup group={selectedGroup} onBack={() => setActivePage('assets')} onSelectDevice={(device) => navigateToDevice(device, { fromGroup: selectedGroup })} />}
          {activePage === 'vulns'  && <Vulnerabilities />}
          {activePage === 'stig'   && <StigCompliance onSelectDevice={navigateToDevice} onSelectGroup={navigateToGroup} />}
          {activePage === 'alerts' && <Alerts />}
          {activePage === 'c2c'    && <ComplyToConnect />}
          {activePage === 'ato'    && <AtoRmf />}
          {activePage === 'export' && <AtoExport />}
          {activePage === 'dmz'    && <DmzTopology onSelectDevice={navigateToDevice} onSelectGroup={navigateToGroup} />}
          {activePage === 'arch'   && <Architecture />}
          {activePage === 'ai'     && <AiAssistant />}
        </Layout>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#0a0e1a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Container maxWidth="xs">
        <Paper
          elevation={6}
          sx={{
            padding: 4,
            backgroundColor: '#111827',
            border: '1px solid #1e3a5f',
            borderRadius: 2,
          }}
        >
          {/* Logo and Title */}
          <Box sx={{ textAlign: 'center', marginBottom: 3 }}>
            <img src={logo} alt="Eighth Technologies" style={{ width: 64, height: 64, borderRadius: '50%', marginBottom: 8 }} />
            <Typography variant="h4" sx={{ color: '#ffffff', fontWeight: 700, letterSpacing: 2 }}>
              ASPYN
            </Typography>
            <Typography variant="body2" sx={{ color: '#7a8fa6', marginTop: 0.5 }}>
              Aggregated Security Platform for Your Network
            </Typography>
          </Box>

          {/* Login Form */}
          <TextField
            fullWidth
            label="Username"
            variant="outlined"
            margin="normal"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            InputLabelProps={{ style: { color: '#7a8fa6' } }}
            InputProps={{ style: { color: '#ffffff' } }}
            sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#1e3a5f' } } }}
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            variant="outlined"
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputLabelProps={{ style: { color: '#7a8fa6' } }}
            InputProps={{ style: { color: '#ffffff' } }}
            sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#1e3a5f' } } }}
          />
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleLogin}
            sx={{
              marginTop: 2,
              backgroundColor: '#00bcd4',
              color: '#0a0e1a',
              fontWeight: 700,
              '&:hover': { backgroundColor: '#0097a7' },
            }}
          >
            Sign In
          </Button>

          <Typography variant="caption" sx={{ color: '#7a8fa6', display: 'block', textAlign: 'center', marginTop: 2 }}>
            Eighth Technologies, LLC — Authorized Users Only
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}

export default App;
